import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Partner } from "@/pages/buyer/BuyerDashboard";

const translations = {
  en: {
    title: "Add Partner",
    subtitle: "Add an external business partner",
    fullName: "Full Name",
    company: "Company",
    email: "Email",
    phone: "Phone",
    specialty: "Expertise / Specialty",
    specialtyPlaceholder: "e.g., Tax Advisor, M&A Lawyer",
    yearsExperience: "Years of Experience",
    description: "Description",
    descriptionPlaceholder: "Brief description (max 500 characters)",
    document: "Document (PDF or image, optional)",
    cancel: "Cancel",
    add: "Add Partner",
    optional: "optional",
    charsLeft: "characters left",
  },
  fr: {
    title: "Ajouter un Partenaire",
    subtitle: "Ajoutez un partenaire commercial externe",
    fullName: "Nom complet",
    company: "Entreprise",
    email: "Email",
    phone: "Téléphone",
    specialty: "Expertise / Spécialité",
    specialtyPlaceholder: "ex: Conseiller fiscal, Avocat M&A",
    yearsExperience: "Années d'expérience",
    description: "Description",
    descriptionPlaceholder: "Brève description (max 500 caractères)",
    document: "Document (PDF ou image, optionnel)",
    cancel: "Annuler",
    add: "Ajouter le Partenaire",
    optional: "optionnel",
    charsLeft: "caractères restants",
  },
};

interface AddPartnerModalProps {
  onClose: () => void;
  onAdd: (data: Partial<Partner> & { documentFile?: File | null }) => void;
  lang: 'en' | 'fr';
}

export function AddPartnerModal({ onClose, onAdd, lang }: AddPartnerModalProps) {
  const t = translations[lang];

  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    specialty: "",
    yearsExperience: "" as string,
    description: "",
  });
  const [documentFile, setDocumentFile] = useState<File | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd({
      name: formData.name,
      company: formData.company,
      email: formData.email,
      phone: formData.phone,
      type: 'external',
      specialty: formData.specialty || undefined,
      yearsExperience: formData.yearsExperience ? Number(formData.yearsExperience) : undefined,
      description: formData.description || undefined,
      documentFile,
    } as any);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>{t.title}</CardTitle>
          <CardDescription>{t.subtitle}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="partnerName">{t.fullName} *</Label>
                <Input
                  id="partnerName"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="partnerCompany">
                  {t.company} <span className="text-muted-foreground text-xs">({t.optional})</span>
                </Label>
                <Input
                  id="partnerCompany"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="partnerEmail">{t.email} *</Label>
                <Input
                  id="partnerEmail"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="partnerPhone">
                  {t.phone} <span className="text-muted-foreground text-xs">({t.optional})</span>
                </Label>
                <Input
                  id="partnerPhone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="partnerSpecialty">
                  {t.specialty} <span className="text-muted-foreground text-xs">({t.optional})</span>
                </Label>
                <Input
                  id="partnerSpecialty"
                  value={formData.specialty}
                  onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                  placeholder={t.specialtyPlaceholder}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="partnerYears">
                  {t.yearsExperience} <span className="text-muted-foreground text-xs">({t.optional})</span>
                </Label>
                <Input
                  id="partnerYears"
                  type="number"
                  min="0"
                  max="99"
                  value={formData.yearsExperience}
                  onChange={(e) => setFormData({ ...formData, yearsExperience: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="partnerDescription">
                {t.description} <span className="text-muted-foreground text-xs">({t.optional})</span>
              </Label>
              <Textarea
                id="partnerDescription"
                value={formData.description}
                maxLength={500}
                rows={4}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t.descriptionPlaceholder}
              />
              <p className="text-xs text-muted-foreground">
                {500 - formData.description.length} {t.charsLeft}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="partnerDocument">{t.document}</Label>
              <Input
                id="partnerDocument"
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t.cancel}
              </Button>
              <Button type="submit">{t.add}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
